import { Article, ArticleType, Category } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { Box, Heading, Loader } from '@wix/design-system';
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';

import { ArticleListSection } from './ArticleListSection';
import { BI } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { isMonthOldLaunchedFr } from '../../../utils/is-month-old-launched-fr';
import { EmptyCategoryState } from '../EmptyCategoryState';

import css from './index.module.scss';

export type ArticleListProps = {
  category: Category;
  categoryArticles?: Article[];
};

export const ArticleList: FunctionComponent<ArticleListProps> = ({
  category,
  categoryArticles,
}) => {
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();

  const onArticleClick = async (article: Article, order: number) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.CATEGORY,
        kb_lang: locale as string,
        item_id: category.id,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.ARTICLE_SELECTION,
        clicked_url: article.url,
        clicked_text: article.title,
        clicked_item_order: `${order + 1}`,
        clicked_item_id: article.id,
      })
    );
  };

  const articles =
    categoryArticles?.filter(
      (article) => article.type === ArticleType.ARTICLE
    ) || [];
  const knownIssues =
    categoryArticles?.filter(
      (article) => article.type === ArticleType.KNOWN_ISSUE
    ) || [];
  const featureRequests =
    (categoryArticles?.filter((article) => !(isMonthOldLaunchedFr(article)))?.filter(
      (article) => article.type === ArticleType.FEATURE_REQUEST
    ) || []);

  const { name } = category;
  const isCategoryEmpty = categoryArticles?.length === 0;
  return (
    <Box
      direction="vertical"
      className={css.wrapper}
      dataHook={DATA_HOOKS.CATEGORY_ARTICLES}
    >
      <Box
        direction="vertical"
        className={css.innerWrapper}
      >
        <Heading
          size="small"
          className={css.title}
          as="h1"
          dataHook={DATA_HOOKS.CATEGORY_ARTICLES_TITLE}
        >
          {name}
        </Heading>
        {categoryArticles ? (
          <>
            <ArticleListSection
              type={ArticleType.ARTICLE}
              articles={articles}
              onClick={onArticleClick}
              startIndex={0}
            />
            <ArticleListSection
              type={ArticleType.KNOWN_ISSUE}
              articles={knownIssues}
              startIndex={articles.length}
              onClick={onArticleClick}
            />
            <ArticleListSection
              type={ArticleType.FEATURE_REQUEST}
              articles={featureRequests}
              startIndex={knownIssues.length + articles.length}
              onClick={onArticleClick}
            />
          </>
        ) : (
          <Box
            align="center"
            verticalAlign="middle"
            className={css.loaderWrapper}
            width="100%"
            marginTop="42px"
          >
            <Loader size="medium" />
          </Box>
        )}

        {isCategoryEmpty ? <EmptyCategoryState /> : null}
      </Box>
    </Box>
  );
};
